import React, {useEffect} from 'react';
import PaginationComponent from '@/components/pagination/PaginationComponent';
import {BaseModal} from '@/components/modals';
import {useStoreActions, useStoreState} from 'easy-peasy';
import axios from 'axios';
import AwesomeDebouncePromise from 'awesome-debounce-promise';
import {buildURL} from '@/utils/urlBuilder';
import CellLoader from '@/components/loading/CellLoader';
import TableComponent from '@/components/table/Table';
import i18n from 'i18n';
import {useFormattedDateTime} from '@/components/utils/FormattedDateTime';

interface UserActivityItem {
    createdOn: string;
    logger: string;
    level: string;
    path: string;
    method: string;
    ip: string;
}

interface UserActivityResponse {
    userActivity: UserActivityItem[];
    pagesList: number[];
}

interface UserActivityModalProps {
    modalOpen: boolean;
    closeModal: () => void;
    activeUserId: number;
}

const userActivityUpdateRequest = (url: string, paramsDict: Record<string, any>): Promise<{data: UserActivityResponse}> =>
    axios.get(url, {params: paramsDict})
        .catch((error: any) => {
            if (error.response?.status === 400) {
                return error.response;
            }
            throw error;
        });

const userActivityUpdateRequestDebounced = AwesomeDebouncePromise(userActivityUpdateRequest, 350);

const UserActivityModal: React.FC<UserActivityModalProps> = React.memo(({
    modalOpen,
    closeModal,
    activeUserId,
}) => {
    const {formatDateTime, isLoaded: configLoaded} = useFormattedDateTime();
    const userActivity = useStoreState((state: any) => state.usersActivity.items);
    const storeUserActivity = useStoreActions((actions: any) => actions.usersActivity.loadUserActivity);

    const searchPagesList = useStoreState((state: any) => state.usersActivity.searchPagesList);
    const storeSearchPagesList = useStoreActions((actions: any) => actions.usersActivity.setSearchPagesList);

    const searchCurrentPage = useStoreState((state: any) => state.usersActivity.searchCurrentPage);
    const storeSearchCurrentPage = useStoreActions((actions: any) => actions.usersActivity.setSearchCurrentPage);

    const loadingUserActivity = useStoreState((state: any) => state.usersActivity.loading);
    const setLoadingUserActivity = useStoreActions((actions: any) => actions.usersActivity.setLoading);

    useEffect(() => {
        const fetchData = async () => {
            setLoadingUserActivity(true);

            const paramsDict = {
                page: searchCurrentPage,
            };

            const response = await userActivityUpdateRequestDebounced(
                buildURL('usersActivity', {obj_id: activeUserId}), paramsDict,
            );

            storeUserActivity(response.data.userActivity);
            storeSearchPagesList(response.data.pagesList);

            setLoadingUserActivity(false);
        };

        if (activeUserId !== 0) {
            fetchData();
        }
    }, [
        searchCurrentPage,
        storeSearchPagesList,
        storeUserActivity,
        activeUserId,
    ]);

    const pageChanged = (e: React.MouseEvent<HTMLElement>) => {
        const target = e.target as HTMLElement;
        if (target.textContent) {
            storeSearchCurrentPage(parseInt(target.textContent, 10));
        }
    };

    const handlePageChange = (page: number) => {
        storeSearchCurrentPage(page);
    };

    const columns = [
        {
            id: 'i18ntthdatecreated5text',
            header: i18n.t('th_date_created.text'),
            accessorFn: (cell: UserActivityItem) => {
                if (!cell.createdOn || cell.createdOn === '-') {
                    return '-';
                }

                return configLoaded ? formatDateTime(cell.createdOn, true, {setZone: false},
                ) : '';
            },
        },
        {
            header: i18n.t('th_logger.text'),
            accessorKey: 'logger',
        },
        {
            header: i18n.t('th_level.text'),
            accessorKey: 'level',
        },
        {
            header: i18n.t('th_path.text'),
            accessorKey: 'path',
            cell: (cell: {getValue: () => string}) => {
                if (!cell.getValue()) {
                    return '-';
                }

                if (cell.getValue().length > 50) {
                    return `${cell.getValue().slice(0, 50)}...`;
                }

                return cell.getValue();
            },
        },
        {
            header: i18n.t('th_method.text'),
            accessorKey: 'method',
        },
        {
            header: i18n.t('th_text_ip.text'),
            accessorKey: 'ip',
        },
    ];

    return (
        <BaseModal
            isOpen={modalOpen}
            onClose={closeModal}
            title={i18n.t('jt_user_activity.text')}
            size="xl"
        >
            {
                loadingUserActivity ? (
                    <CellLoader />
                ) : (
                    <>
                        <div className="card">
                            <div className="card-body">
                                <TableComponent data={userActivity} columns={columns} />
                                <PaginationComponent
                                    pages={searchPagesList}
                                    currentPage={searchCurrentPage}
                                    onPageChange={handlePageChange}
                                />
                            </div>
                        </div>
                    </>
                )
            }
        </BaseModal>
    );
});

UserActivityModal.displayName = 'UserActivityModal';

export default UserActivityModal; 